import React from 'react'
import { motion } from 'framer-motion'
import { Box, Typography, Grid, useMediaQuery } from '@material-ui/core'
import { Email, Phone, LinkedIn, GitHub, Language } from '@material-ui/icons'

const HeroSection = ({ personalInfo, technicalLeadership }) => {
  const isMobile = useMediaQuery('(max-width: 480px)')
  const isTablet = useMediaQuery('(max-width: 768px)')
  const isSmallDesktop = useMediaQuery('(max-width: 1024px)')
  const contactItems = [
    { icon: Email, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: Phone, label: 'Mobile', value: personalInfo.phone, href: `tel:${personalInfo.phone}` },
    { icon: LinkedIn, label: 'LinkedIn', value: '', href: `https://${personalInfo.linkedin}` },
    { icon: GitHub, label: 'GitHub', value: '', href: `https://${personalInfo.github}` },
    { icon: Language, label: 'Blog', value: '', href: `https://${personalInfo.blog}` }
  ]

  return (
    <Box style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      background: 'linear-gradient(135deg, rgba(5, 5, 15, 0.98) 0%, rgba(10, 10, 20, 0.98) 100%)',
      padding: isMobile ? '2rem 1rem' : '4rem 2rem'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        style={{ textAlign: 'center', marginBottom: '3rem' }}
      >
        <Typography style={{
          fontFamily: '"Orbitron", monospace',
          fontSize: 'clamp(3rem, 8vw, 5rem)',
          fontWeight: 'bold',
          background: 'linear-gradient(90deg, #00ffff 0%, #ff00ff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '1rem',
          textShadow: '0 0 60px rgba(0, 255, 255, 0.3)',
          letterSpacing: '0.05em'
        }}>
          {personalInfo.name}
        </Typography>

        <Typography style={{
          fontFamily: '"Share Tech Mono", monospace',
          fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
          color: '#00ffff',
          textShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
          letterSpacing: '0.2em',
          marginBottom: '2rem'
        }}>
          {personalInfo.title}
        </Typography>
      </motion.div>

      {/* Interactive Grid Dashboard */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.6 }}
        style={{
          width: '100%',
          maxWidth: '1000px',
          marginBottom: '3rem',
          position: 'relative'
        }}
      >

        <Box style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? 'repeat(1, 1fr)'
            : isTablet
              ? 'repeat(2, 1fr)'
              : 'repeat(5, 1fr)',
          gap: isMobile ? '0.75rem' : '1rem',
          padding: isMobile ? '1rem' : '1.5rem'
        }}>
          {contactItems.map((item, index) => {
            const colors = ['#00ffff', '#ff00ff', '#00ff88', '#ffaa00', '#ff0088']
            const currentColor = colors[index % colors.length]

            return (
              <motion.a
                key={index}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.15 }}
                whileHover={{
                  scale: 1.05,
                  y: -10,
                  rotateY: 5,
                  boxShadow: `0 20px 40px rgba(${currentColor === '#00ffff' ? '0,255,255' : currentColor === '#ff00ff' ? '255,0,255' : currentColor === '#00ff88' ? '0,255,136' : currentColor === '#ffaa00' ? '255,170,0' : '255,0,136'},0.4)`
                }}
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  background: `linear-gradient(145deg, rgba(0,0,0,0.8) 0%, rgba(20,20,40,0.6) 100%)`,
                  border: `0.1px solid ${currentColor}40`,
                  borderRadius: '12px',
                  padding: isMobile ? '1rem' : '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '0.5rem' : '0.75rem',
                  minHeight: isMobile ? '100px' : '90px',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  cursor: 'pointer',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = currentColor
                  e.currentTarget.style.background = `linear-gradient(145deg, ${currentColor}15 0%, rgba(0,0,0,0.8) 100%)`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${currentColor}40`
                  e.currentTarget.style.background = 'linear-gradient(145deg, rgba(0,0,0,0.8) 0%, rgba(20,20,40,0.6) 100%)'
                }}
              >
                {/* Floating particles effect */}
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: '6px',
                  height: '6px',
                  background: currentColor,
                  borderRadius: '50%',
                  boxShadow: `0 0 15px ${currentColor}`,
                  animation: 'pulse 2s infinite'
                }}/>

                {/* Metric-style corner accent */}
                <div style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '20px',
                  height: '20px',
                  borderTop: `2px solid ${currentColor}`,
                  borderLeft: `2px solid ${currentColor}`,
                  borderTopLeftRadius: '12px'
                }}/>

                <item.icon style={{
                  fontSize: isMobile ? '1.5rem' : '1.8rem',
                  color: currentColor,
                  filter: `drop-shadow(0 0 10px ${currentColor})`
                }}/>

                <Box style={{ textAlign: 'center' }}>
                  <Typography style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: isMobile ? '0.65rem' : '0.7rem',
                    color: '#888',
                    marginBottom: '0.2rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase'
                  }}>
                    {item.label}
                  </Typography>

                  <Typography style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    color: '#ffffff',
                    textShadow: `0 0 8px ${currentColor}`,
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                    wordBreak: 'break-all'
                  }}>
                    {item.value}
                  </Typography>
                </Box>

                {/* Bottom right corner accent */}
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '15px',
                  height: '15px',
                  borderBottom: `2px solid ${currentColor}60`,
                  borderRight: `2px solid ${currentColor}60`,
                  borderBottomRightRadius: '12px'
                }}/>
              </motion.a>
            )
          })}
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.2 }}
        style={{
          width: '100%',
          maxWidth: '1000px',
          background: 'rgba(0, 0, 0, 0.5)',
          border: '2px solid rgba(0, 255, 255, 0.3)',
          borderRadius: '12px',
          padding: isMobile ? '1.5rem' : '2rem',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Typography style={{
          fontFamily: '"Orbitron", monospace',
          fontSize: 'clamp(1.2rem, 3.5vw, 1.7rem)',
          color: '#ff00ff',
          marginBottom: isMobile ? '1rem' : '1.5rem',
          textAlign: 'center',
          textShadow: '0 0 10px rgba(255, 0, 255, 0.5)'
        }}>
          ⭐ TECHNICAL LEADERSHIP
        </Typography>

        <Box style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {technicalLeadership.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.4 + index * 0.2 }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}
            >
              <span style={{
                color: '#00ffff',
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
                flexShrink: 0
              }}>●</span>
              <Typography style={{
                fontFamily: '"Inter", "Roboto", sans-serif',
                fontSize: 'clamp(1rem, 2.8vw, 1.3rem)',
                color: '#cccccc',
                textShadow: '0 0 3px #00ffff, 0 1px 2px rgba(0,0,0,0.8)',
                fontWeight: '500',
                lineHeight: 1.6
              }}>
                {point}
              </Typography>
            </motion.div>
          ))}
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        style={{
          position: 'absolute',
          bottom: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <Typography style={{
          fontFamily: '"Share Tech Mono", monospace',
          fontSize: '0.8rem',
          color: '#00ffff',
          letterSpacing: '0.2em',
          textShadow: '0 0 10px rgba(0, 255, 255, 0.5)'
        }}>
          SCROLL TO EXPLORE
        </Typography>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            fontSize: '2rem',
            color: '#00ffff',
            textShadow: '0 0 20px rgba(0, 255, 255, 0.8)'
          }}
        >
          ↓
        </motion.div>
      </motion.div>
    </Box>
  )
}

export default HeroSection
